import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { CheckSquare, Loader2, Paperclip, SendHorizontal, Smile } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/UserAvatar";
import EmojiPickerPopover from "@/components/onboarding/EmojiPickerPopover";
import { useChat } from "@/contexts/ChatContext";
import { getApiErrorMessage } from "@/lib/api";
import { AdjuntoTile, useSubidaAdjuntos } from "@/components/chat/Adjuntos";
import { cn } from "@/lib/utils";

const ESCRIBIENDO_CADA_MS = 2500;

/** Busca un «@texto» justo antes del cursor para abrir el selector de menciones. */
function mencionEnCursor(texto, cursor) {
  const antes = texto.slice(0, cursor);
  const m = antes.match(/(?:^|\s)@([\p{L}\p{N}._-]{0,30})$/u);
  return m ? { query: m[1].toLowerCase(), inicio: cursor - m[1].length - 1 } : null;
}

/**
 * Compositor: Enter envía (Mayús+Enter salto de línea), @ menciona, clip/arrastrar/pegar adjunta,
 * y el botón de check convierte lo escrito en un trabajo pendiente.
 */
export const Composer = forwardRef(function Composer({ clienteId, miembros, yo, onEnviar, onCrearTrabajo, disabled }, ref) {
  const { usuariosByEmail, send } = useChat();
  const textareaRef = useRef(null);
  const fileRef = useRef(null);
  const ultimoEscribiendoRef = useRef(0);
  const [texto, setTexto] = useState("");
  const [menciones, setMenciones] = useState({}); // nombre → email
  const subida = useSubidaAdjuntos(clienteId);
  const [mencion, setMencion] = useState(null);
  const [mencionIdx, setMencionIdx] = useState(0);
  const [enviando, setEnviando] = useState(false);

  const candidatos = useMemo(() => {
    if (!mencion) return [];
    return miembros
      .filter((e) => e !== yo)
      .map((e) => usuariosByEmail[e] || { email: e, nombre: e.split("@")[0] })
      .filter((u) => !mencion.query || u.nombre.toLowerCase().includes(mencion.query) || u.email.startsWith(mencion.query))
      .slice(0, 6);
  }, [mencion, miembros, usuariosByEmail, yo]);

  const autoSize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  useImperativeHandle(ref, () => ({
    subirArchivos: subida.subir,
    focus: () => textareaRef.current?.focus(),
  }));

  const elegirMencion = (u) => {
    const el = textareaRef.current;
    const cursor = el?.selectionStart ?? texto.length;
    const nuevo = `${texto.slice(0, mencion.inicio)}@${u.nombre} ${texto.slice(cursor)}`;
    setTexto(nuevo);
    setMenciones((prev) => ({ ...prev, [u.nombre]: u.email }));
    setMencion(null);
    requestAnimationFrame(() => {
      const pos = mencion.inicio + u.nombre.length + 2;
      el?.focus();
      el?.setSelectionRange(pos, pos);
      autoSize();
    });
  };

  const enviar = async () => {
    const t = texto.trim();
    const listos = subida.listos;
    if ((!t && listos.length === 0) || enviando) return;
    if (subida.subiendo) {
      toast.message("Espera a que terminen de subirse los archivos");
      return;
    }
    const emails = Object.entries(menciones)
      .filter(([nombre]) => t.includes(`@${nombre}`))
      .map(([, email]) => email);
    setEnviando(true);
    try {
      await onEnviar({ texto: t, menciones: emails, adjuntos: listos.map((a) => a.id) });
      setTexto("");
      setMenciones({});
      subida.reset();
      requestAnimationFrame(autoSize);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo enviar"));
    } finally {
      setEnviando(false);
      textareaRef.current?.focus();
    }
  };

  const onChange = (e) => {
    setTexto(e.target.value);
    autoSize();
    const m = mencionEnCursor(e.target.value, e.target.selectionStart);
    setMencion(m);
    setMencionIdx(0);
    const ahora = Date.now();
    if (e.target.value && ahora - ultimoEscribiendoRef.current > ESCRIBIENDO_CADA_MS) {
      ultimoEscribiendoRef.current = ahora;
      send({ type: "escribiendo", cliente_id: clienteId });
    }
  };

  const onKeyDown = (e) => {
    if (mencion && candidatos.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMencionIdx((i) => (i + 1) % candidatos.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMencionIdx((i) => (i - 1 + candidatos.length) % candidatos.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        elegirMencion(candidatos[mencionIdx]);
        return;
      }
      if (e.key === "Escape") {
        setMencion(null);
        return;
      }
    }
    // En móvil Enter hace salto de línea (se envía con el botón); en escritorio Enter envía
    const esTactil = window.matchMedia?.("(pointer: coarse)").matches;
    if (e.key === "Enter" && !e.shiftKey && !esTactil && !e.nativeEvent.isComposing) {
      e.preventDefault();
      enviar();
    }
  };

  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) {
      e.preventDefault();
      subida.subir(files);
    }
  };

  const insertarEmoji = (emoji) => {
    const el = textareaRef.current;
    const ini = el?.selectionStart ?? texto.length;
    const fin = el?.selectionEnd ?? texto.length;
    setTexto(texto.slice(0, ini) + emoji + texto.slice(fin));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(ini + emoji.length, ini + emoji.length);
    });
  };

  const puedeEnviar = (texto.trim() || subida.listos.length > 0) && !enviando;

  return (
    <div className="shrink-0 border-t bg-background px-2 md:px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] relative">
      {mencion && candidatos.length > 0 && (
        <ul className="absolute bottom-full left-2 md:left-4 mb-2 w-64 rounded-xl border bg-popover shadow-lg p-1 z-20">
          {candidatos.map((u, i) => (
            <li key={u.email}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  elegirMencion(u);
                }}
                className={cn("w-full flex items-center gap-2.5 rounded-lg px-2 h-10 text-sm text-left", i === mencionIdx ? "bg-muted" : "hover:bg-muted/60")}
              >
                <UserAvatar user={u} size={26} />
                <span className="truncate">{u.nombre}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {subida.items.length > 0 && (
        <div className="flex gap-2.5 overflow-x-auto pt-1.5 pb-2 px-1">
          {subida.items.map((it) => (
            <AdjuntoTile
              key={it.tmpId}
              className="w-16 shrink-0"
              adjunto={it.meta || { nombre: it.nombre, mime: it.mime }}
              preview={it.preview}
              subiendo={it.estado === "subiendo"}
              onQuitar={() => subida.quitar(it.tmpId)}
            />
          ))}
        </div>
      )}
      <div className="flex items-end gap-1">
        <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-muted-foreground" onClick={() => fileRef.current?.click()} disabled={disabled} title="Adjuntar">
          <Paperclip className="w-5 h-5" />
        </Button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { subida.subir(e.target.files); e.target.value = ""; }} />
        <div className="flex-1 min-w-0 flex items-end rounded-2xl border bg-muted/30 focus-within:bg-background focus-within:border-foreground/20 transition-colors">
          <textarea
            ref={textareaRef}
            rows={1}
            value={texto}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onBlur={() => setTimeout(() => setMencion(null), 150)}
            placeholder="Escribe un mensaje…"
            disabled={disabled}
            className="flex-1 min-w-0 resize-none bg-transparent px-3.5 py-2.5 text-[15px] md:text-sm outline-none max-h-40 leading-5"
          />
          <EmojiPickerPopover onSelect={insertarEmoji}>
            <button type="button" className="hidden md:flex h-10 w-9 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground" title="Emoji">
              <Smile className="w-5 h-5" />
            </button>
          </EmojiPickerPopover>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0 text-muted-foreground"
          onClick={() => {
            onCrearTrabajo(texto.trim());
            if (texto.trim()) {
              setTexto("");
              requestAnimationFrame(autoSize);
            }
          }}
          disabled={disabled}
          title="Convertir en trabajo pendiente"
        >
          <CheckSquare className="w-5 h-5" />
        </Button>
        <Button type="button" size="icon" className="h-10 w-10 shrink-0 rounded-full" onClick={enviar} disabled={!puedeEnviar || disabled} title="Enviar">
          {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizontal className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
});
